import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Search,
  ArrowUp,
  ArrowDown,
  Trophy,
  Wallet,
  BarChart3,
  RefreshCw,
  Loader2,
  Play,
  ChevronLeft,
  AlertCircle,
  Info,
  Sparkles,
  PieChart,
  Clock,
  Percent,
  Star,
  StarOff,
  Eye,
  Activity,
  Filter,
  LogIn,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import AdviceCarousel from "./AdviceCarousel";
import FunFactsLoader from "./FunFactsLoader";


const API_URL = process.env.REACT_APP_BACKEND_URL;
const USD_TO_CAD = 1.36;

const StockSimulator = ({ onBack, hideInternalBackButton = false }) => {
  const [user, setUser] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfolioChecked, setPortfolioChecked] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [allStocks, setAllStocks] = useState([]); // All stocks for search/display
  const [watchlist, setWatchlist] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedStockDetails, setSelectedStockDetails] = useState(null);
  const [stockInsights, setStockInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [tradeAmount, setTradeAmount] = useState("");
  const [tradeMode, setTradeMode] = useState("shares"); // "shares" or "cad"
  const [tradeLoading, setTradeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("portfolio");
  const [personalizedTip, setPersonalizedTip] = useState(null);
  const [showInCAD, setShowInCAD] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [creatingPortfolio, setCreatingPortfolio] = useState(false);
  // AI Search state
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");

  // Check for logged in user on mount
  useEffect(() => {
    const userData = localStorage.getItem("user_data") || sessionStorage.getItem("user_data");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error("Failed to parse user data");
      }
    }
    setLoading(false);
  }, []);

  // Load portfolio when user is available
  useEffect(() => {
    if (user && !portfolioChecked) {
      checkExistingPortfolio();
    }
  }, [user, portfolioChecked]);

  // Auto-refresh portfolio every 4 seconds for accurate values
  useEffect(() => {
    if (!user || !autoRefresh || !portfolio) return;
    
    const interval = setInterval(() => {
      loadPortfolioQuiet();
    }, 4000); // 4 seconds for portfolio updates
    
    return () => clearInterval(interval);
  }, [user, autoRefresh, portfolio]);

  // Auto-refresh stock prices every 30 seconds
  useEffect(() => {
    if (!user || !autoRefresh || !portfolio || stocks.length === 0) return;
    
    const interval = setInterval(() => {
      if (stocks.length > 0) loadStocksQuiet();
    }, 30000); // 30 seconds for stock list updates
    
    return () => clearInterval(interval);
  }, [user, autoRefresh, portfolio, stocks.length]);

  // Check if user already has a portfolio
  const checkExistingPortfolio = async () => {
    setPortfolioLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/simulator/portfolio/${user.id}`);
      setPortfolio(response.data);
      setPortfolioChecked(true);
      // Load additional data in background
      loadStocks();
      loadWatchlist();
      loadLeaderboard();
      loadTransactions();
      loadPersonalizedTip();
      loadMarketStatus();
    } catch (error) {
      if (error.response?.status === 404) {
        setPortfolio(null);
        setPortfolioChecked(true);
        // Still load market status for new users
        loadMarketStatus();
      }
    } finally {
      setPortfolioLoading(false);
    }
  };

  const loadMarketStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/simulator/market-status`);
      setMarketStatus(response.data);
    } catch (error) {
      console.error("Failed to load market status");
    }
  };

  // Quiet refresh (no loading indicator)
  const loadPortfolioQuiet = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/simulator/portfolio/${user.id}`);
      setPortfolio(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Failed to refresh portfolio");
    }
  };

  const loadPortfolio = async () => {
    if (!user) return;
    setPortfolioLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/simulator/portfolio/${user.id}`);
      setPortfolio(response.data);
      setLastUpdate(new Date());
      loadTransactions();
    } catch (error) {
      if (error.response?.status === 404) {
        setPortfolio(null);
      }
    } finally {
      setPortfolioLoading(false);
    }
  };

  const loadStocksQuiet = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/simulator/stocks?limit=200`);
      setStocks(response.data.stocks || []);
      setMarketStatus(response.data.market_status);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Failed to refresh stocks");
    }
  };

  // Load ALL stock names (for search/display - faster, no prices)
  const loadAllStocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/simulator/stocks/all`);
      setAllStocks(response.data.stocks || []);
    } catch (error) {
      console.error("Failed to load all stocks");
    }
  };

  const loadStocks = async () => {
    setStocksLoading(true);
    try {
      // Load all stocks first (with names, for search)
      if (allStocks.length === 0) {
        loadAllStocks();
      }
      
      const url = categoryFilter && categoryFilter !== "all" 
        ? `${API_URL}/api/simulator/stocks?category=${categoryFilter}&limit=200`
        : `${API_URL}/api/simulator/stocks?limit=200`;
      const response = await axios.get(url);
      setStocks(response.data.stocks || []);
      setMarketStatus(response.data.market_status);
      setLastUpdate(new Date());
    } catch (error) {
      toast.error("Failed to load stocks");
    } finally {
      setStocksLoading(false);
    }
  };

  const loadWatchlist = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/simulator/watchlist/${user.id}`);
      setWatchlist(response.data.watchlist || []);
    } catch (error) {
      console.error("Failed to load watchlist");
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/simulator/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to load leaderboard");
    }
  };

  const loadTransactions = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/simulator/transactions/${user.id}`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Failed to load transactions");
    }
  };

  const loadPersonalizedTip = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/tips/personalized/${user.id}?context=stock-simulator`);
      setPersonalizedTip(response.data);
    } catch (error) {
      console.error("Failed to load tip");
    }
  };

  // AI-powered stock search
  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    
    setAiSearching(true);
    setAiSearchResults(null);
    setAiExplanation("");
    
    try {
      const response = await axios.post(`${API_URL}/api/simulator/ai-search`, {
        query: searchQuery
      });
      
      if (response.data.results?.length > 0) {
        setAiSearchResults(response.data.results);
        setAiExplanation(response.data.explanation || "");
        if (response.data.ai_powered) {
          toast.success(`🤖 AI found ${response.data.results.length} matching stocks!`);
        }
      } else {
        toast.info("No stocks found matching your query");
      }
    } catch (error) {
      console.error("AI search error:", error);
      toast.error("AI search failed");
    } finally {
      setAiSearching(false);
    }
  };

  // Clear AI search results
  const clearAISearch = () => {
    setAiSearchResults(null);
    setAiExplanation("");
    setSearchQuery("");
  };

  // Create new portfolio - INSTANT FEEDBACK
  const createPortfolio = async () => {
    if (!user) {
      toast.error("Please log in first");
      return;
    }
    
    setCreatingPortfolio(true);
    toast.loading("Creating your portfolio...", { id: "create-portfolio" });
    
    try {
      const response = await axios.post(`${API_URL}/api/simulator/portfolio/create`, {
        user_id: user.id,
      });
      
      // Immediately set portfolio
      setPortfolio(response.data.portfolio);
      toast.success("🎉 Portfolio created! You have $10,000 CAD to invest!", { id: "create-portfolio" });
      
      // Load stocks in background
      loadStocks();
      loadMarketStatus();
      
      // Log activity
      axios.post(`${API_URL}/api/activity/log`, {
        user_id: user.id,
        activity_type: "portfolio_created",
        activity_data: { tool: "simulator" }
      }).catch(() => {});
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create portfolio";
      toast.error(message, { id: "create-portfolio" });
    } finally {
      setCreatingPortfolio(false);
    }
  };

  // Execute trade - INSTANT FEEDBACK
  const executeTrade = async (action) => {
    if (!selectedStock || !tradeAmount || parseFloat(tradeAmount) <= 0) {
      toast.error("Please select a stock and enter a valid amount");
      return;
    }

    setTradeLoading(true);
    const toastId = `trade-${Date.now()}`;
    toast.loading(`${action === "buy" ? "Buying" : "Selling"} ${selectedStock.symbol}...`, { id: toastId });
    
    try {
      await axios.post(`${API_URL}/api/simulator/trade`, {
        user_id: user.id,
        symbol: selectedStock.symbol,
        shares: parseFloat(tradeAmount),
        action,
      });
      
      toast.success(`✅ ${action === "buy" ? "Bought" : "Sold"} ${tradeAmount} shares of ${selectedStock.symbol}!`, { id: toastId });
      setTradeAmount("");
      setSelectedStock(null);
      
      // Refresh portfolio immediately
      loadPortfolio();
      loadLeaderboard();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} stock`, { id: toastId });
    } finally {
      setTradeLoading(false);
    }
  };

  // Watchlist functions
  const addToWatchlist = async (symbol) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/api/simulator/watchlist/add`, {
        user_id: user.id,
        symbol: symbol
      });
      toast.success(`Added ${symbol} to watchlist`);
      loadWatchlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to watchlist");
    }
  };

  const removeFromWatchlist = async (symbol) => {
    if (!user) return;
    try {
      await axios.delete(`${API_URL}/api/simulator/watchlist/${user.id}/${symbol}`);
      toast.success(`Removed ${symbol} from watchlist`);
      loadWatchlist();
    } catch (error) {
      toast.error("Failed to remove from watchlist");
    }
  };

  const isInWatchlist = (symbol) => watchlist.some(item => item.symbol === symbol);

  // Load detailed stock info when selected
  const selectStock = async (stock) => {
    setSelectedStock(stock);
    setSelectedStockDetails(null);
    setStockInsights(null);
    setDetailsLoading(true);
    setInsightsLoading(true);
    
    // Fetch basic details
    try {
      const response = await axios.get(`${API_URL}/api/simulator/stock/${stock.symbol}`);
      setSelectedStockDetails(response.data);
    } catch (error) {
      console.error("Failed to load stock details");
    } finally {
      setDetailsLoading(false);
    }
    
    // Fetch AI-powered insights from Perplexity (in parallel)
    try {
      const insightsResponse = await axios.get(`${API_URL}/api/simulator/stock/${stock.symbol}/insights`);
      setStockInsights(insightsResponse.data);
    } catch (error) {
      console.error("Failed to load stock insights");
    } finally {
      setInsightsLoading(false);
    }
  };

  const quickSell = (holding) => {
    const stockData = stocks.find(s => s.symbol === holding.symbol);
    if (stockData) {
      selectStock(stockData);
      setTradeAmount(holding.shares.toString());
      setActiveTab("trade");
    }
  };

  const quickBuyMore = (holding) => {
    const stockData = stocks.find(s => s.symbol === holding.symbol);
    if (stockData) {
      selectStock(stockData);
      setTradeAmount("");
      setActiveTab("trade");
    }
  };

  const toCAD = (value, currency) => {
    if (currency === "CAD" || !showInCAD) return value;
    return value * USD_TO_CAD;
  };

  const formatCurrency = (value, currency = "USD") => {
    const convertedValue = toCAD(value, currency);
    return `$${convertedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatLargeNumber = (num) => {
    if (!num) return "N/A";
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return "N/A";
    return `${(num * 100).toFixed(2)}%`;
  };

  // Enhanced filtering - search by ticker OR name, case insensitive
  // Also applies category filter
  const filteredStocks = stocks.filter((stock) => {
    // Apply search query
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      stock.symbol.toLowerCase().includes(query) ||
      stock.name.toLowerCase().includes(query);
    
    if (!matchesSearch) return false;
    
    // Apply category filter
    if (categoryFilter === "all") return true;
    
    // Define gold and silver stocks
    const goldStocks = ["ABX.TO", "AEM.TO", "K.TO", "WPM.TO", "FNV.TO", "AGI.TO", "XGD.TO", "ZGD.TO", "CGL.TO"];
    const silverStocks = ["PAAS.TO", "AG.TO", "EDR.TO", "SVR.TO"];
    
    const categories = {
      tech: ["AAPL", "MSFT", "GOOGL", "GOOG", "AMZN", "NVDA", "META", "TSLA", "AMD", "INTC", "CRM", "ORCL", "ADBE", "NFLX", "PYPL", "UBER", "ABNB", "SNAP", "PINS", "ROKU", "ZM", "DOCU", "SPLK", "OKTA", "CRWD", "ZS", "NET", "DDOG", "MDB", "SNOW", "PLTR", "COIN", "HOOD", "RBLX", "U", "SPOT", "SQ", "AFRM", "NOW", "INTU", "TEAM", "WDAY", "PANW", "FTNT", "CYBR", "TTD", "BILL", "HUBS", "VEEV", "CDNS", "SNPS", "ANSS", "KLAC", "LRCX", "AMAT", "ASML", "MU"],
      finance: ["JPM", "BAC", "WFC", "GS", "MS", "C", "USB", "PNC", "TFC", "SCHW", "V", "MA", "AXP", "BLK", "BX", "KKR", "APO", "SPGI", "MCO", "ICE"],
      healthcare: ["JNJ", "UNH", "PFE", "ABBV", "MRK", "TMO", "ABT", "LLY", "BMY", "AMGN", "GILD", "REGN", "VRTX", "BIIB", "MRNA", "ISRG", "DHR", "MDT", "SYK", "BSX"],
      canadian: stock.symbol.includes(".TO") && stock.type !== "ETF",
      etf: stock.type === "ETF",
      crypto: ["COIN", "MSTR", "MARA", "RIOT", "HUT", "BITF", "CLSK"].includes(stock.symbol),
      gold: goldStocks.includes(stock.symbol),
      silver: silverStocks.includes(stock.symbol),
    };
    
    if (categoryFilter === "canadian") return categories.canadian;
    if (categoryFilter === "etf") return categories.etf;
    if (categoryFilter === "gold") return categories.gold;
    if (categoryFilter === "silver") return categories.silver;
    return categories[categoryFilter]?.includes(stock.symbol);
  });

  // Gold and silver stock symbols for grouping
  const goldStocks = ["ABX.TO", "AEM.TO", "K.TO", "WPM.TO", "FNV.TO", "AGI.TO", "XGD.TO", "ZGD.TO", "CGL.TO"];
  const silverStocks = ["PAAS.TO", "AG.TO", "EDR.TO", "SVR.TO"];

  // Group stocks by category for display (counts)
  const groupedStocks = {
    tech: stocks.filter(s => ["AAPL", "MSFT", "GOOGL", "GOOG", "AMZN", "NVDA", "META", "TSLA", "AMD", "INTC", "CRM", "ORCL", "ADBE", "NFLX", "PYPL", "UBER", "ABNB", "SNAP", "PINS", "ROKU", "ZM", "DOCU", "SPLK", "OKTA", "CRWD", "ZS", "NET", "DDOG", "MDB", "SNOW", "PLTR", "COIN", "HOOD", "RBLX", "U", "SPOT", "SQ", "AFRM", "NOW", "INTU", "TEAM", "WDAY", "PANW", "FTNT", "CYBR", "TTD", "BILL", "HUBS", "VEEV", "CDNS", "SNPS", "ANSS", "KLAC", "LRCX", "AMAT", "ASML", "MU"].includes(s.symbol)),
    finance: stocks.filter(s => ["JPM", "BAC", "WFC", "GS", "MS", "C", "USB", "PNC", "TFC", "SCHW", "V", "MA", "AXP", "BLK", "BX", "KKR", "APO", "SPGI", "MCO", "ICE"].includes(s.symbol)),
    healthcare: stocks.filter(s => ["JNJ", "UNH", "PFE", "ABBV", "MRK", "TMO", "ABT", "LLY", "BMY", "AMGN", "GILD", "REGN", "VRTX", "BIIB", "MRNA", "ISRG", "DHR", "MDT", "SYK", "BSX"].includes(s.symbol)),
    canadian: stocks.filter(s => s.symbol.includes(".TO") && s.type !== "ETF"),
    etf: stocks.filter(s => s.type === "ETF"),
    crypto: stocks.filter(s => ["COIN", "MSTR", "MARA", "RIOT", "HUT", "BITF", "CLSK"].includes(s.symbol)),
    gold: stocks.filter(s => goldStocks.includes(s.symbol)),
    silver: stocks.filter(s => silverStocks.includes(s.symbol)),
  };

  const calculateTodayChange = () => {
    if (!portfolio || !portfolio.holdings?.length) return { amount: 0, percent: 0 };
    
    let todayChange = 0;
    portfolio.holdings.forEach(holding => {
      todayChange += (holding.current_value * (holding.day_change_pct || 0)) / 100;
    });
    
    const totalValue = portfolio.total_value || 10000;
    const todayPercent = (todayChange / totalValue) * 100;
    
    return { amount: todayChange, percent: todayPercent };
  };

  const todayChange = calculateTodayChange();

  // LOADING STATE
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FunFactsLoader message="Starting simulator..." />
      </div>
    );
  }

  // NOT LOGGED IN
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Card className="text-center p-8" data-testid="login-required-card">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Login Required</h2>
          <p className="text-slate-600 mb-6">
            Please log in to use the Stock Market Simulator and compete on the leaderboard.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={onBack} variant="outline">
              <ChevronLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // CHECKING FOR EXISTING PORTFOLIO
  if (portfolioLoading && !portfolioChecked) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <FunFactsLoader message="Loading your portfolio..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6" data-testid="stock-simulator">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          {!hideInternalBackButton && (
            <Button variant="ghost" onClick={onBack} className="hover:bg-slate-100 active:scale-95 transition-transform p-2" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-slate-800">Stock Market Simulator</h1>
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600">
              <span>Practice with $10,000 CAD</span>
              {marketStatus && (
                <Badge className={`text-xs ${marketStatus.nyse_open ? "bg-emerald-500" : "bg-slate-500"}`}>
                  <Activity className="w-3 h-3 mr-1" />
                  {marketStatus.message}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={showInCAD ? "default" : "outline"}
            size="sm"
            onClick={() => setShowInCAD(true)}
            className={`active:scale-95 transition-transform text-xs ${showInCAD ? "bg-red-600 hover:bg-red-700" : ""}`}
          >
            🇨🇦 CAD
          </Button>
          <Button
            variant={!showInCAD ? "default" : "outline"}
            size="sm"
            onClick={() => setShowInCAD(false)}
            className={`active:scale-95 transition-transform text-xs ${!showInCAD ? "bg-blue-600 hover:bg-blue-700" : ""}`}
          >
            🇺🇸 USD
          </Button>
          {portfolio && (
            <>
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`active:scale-95 transition-transform ${autoRefresh ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                title="Auto-refresh portfolio every 4 seconds"
              >
                <RefreshCw className={`w-4 h-4 ${autoRefresh ? "animate-spin" : ""}`} style={{ animationDuration: "3s" }} />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { loadStocks(); loadPortfolio(); loadWatchlist(); }} 
                disabled={stocksLoading || portfolioLoading}
                className="active:scale-95 transition-transform hidden sm:flex"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${stocksLoading || portfolioLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Last Update */}
      {lastUpdate && portfolio && (
        <div className="flex items-center justify-end text-xs text-slate-500 gap-2">
          <Clock className="w-3 h-3" />
          Last updated: {lastUpdate.toLocaleTimeString()}
          {autoRefresh && <span className="text-emerald-600">(portfolio: 4s, prices: 30s)</span>}
        </div>
      )}

      {/* Personalized Tip */}
      {personalizedTip && portfolio && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold text-emerald-800">{personalizedTip.tip_title}</h4>
              <p className="text-sm text-emerald-700">{personalizedTip.tip_content}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Advice Carousel - 30 tips rotating every 3 minutes */}
      {portfolio && !personalizedTip && (
        <AdviceCarousel />
      )}

      {/* NO PORTFOLIO - CREATE ONE */}
      {!portfolio ? (
        <Card className="text-center p-12 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Play className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Start Your Investment Journey</h2>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Get $10,000 CAD in virtual cash to practice investing in 150+ real stocks and ETFs. 
            Track the NYSE, NASDAQ, and TSX in real-time!
          </p>
          <Button
            onClick={createPortfolio}
            disabled={creatingPortfolio}
            className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6 active:scale-95 transition-all duration-150 disabled:opacity-70"
            data-testid="create-portfolio-btn"
          >
            {creatingPortfolio ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating Portfolio...
              </>
            ) : (
              <>
                <Wallet className="w-5 h-5 mr-2" />
                Get My $10,000 CAD
              </>
            )}
          </Button>
          
          <p className="text-xs text-slate-500 mt-4">
            Logged in as: {user.email}
          </p>
        </Card>
      ) : (
        <>
          {/* PORTFOLIO DASHBOARD */}
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
                <div>
                  <p className="text-slate-400 text-sm mb-1 flex items-center gap-2">
                    <PieChart className="w-4 h-4" /> Total Portfolio Value
                  </p>
                  <h2 className="text-4xl md:text-5xl font-bold" data-testid="total-value">
                    {formatCurrency(portfolio.total_value || 10000, "USD")}
                    <span className="text-lg ml-2 text-slate-400">{showInCAD ? "CAD" : "USD"}</span>
                  </h2>
                </div>
                
                <div className={`p-4 rounded-xl ${todayChange.amount >= 0 ? "bg-emerald-500/20" : "bg-red-500/20"}`}>
                  <p className="text-slate-300 text-sm mb-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Today&apos;s Change
                  </p>
                  <div className="flex items-center gap-3">
                    {todayChange.amount >= 0 ? (
                      <TrendingUp className="w-8 h-8 text-emerald-400" />
                    ) : (
                      <TrendingDown className="w-8 h-8 text-red-400" />
                    )}
                    <div>
                      <p className={`text-2xl font-bold ${todayChange.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {todayChange.percent >= 0 ? "+" : ""}{todayChange.percent.toFixed(2)}%
                      </p>
                      <p className={`text-sm ${todayChange.amount >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {todayChange.amount >= 0 ? "+" : ""}${Math.abs(toCAD(todayChange.amount, "USD")).toFixed(2)} {showInCAD ? "CAD" : "USD"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-300 text-sm mb-1">
                    <DollarSign className="w-4 h-4" /> Cash Available
                  </div>
                  <p className="text-xl font-bold text-white" data-testid="cash-available">
                    {formatCurrency(portfolio.cash || 0, "USD")}
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-300 text-sm mb-1">
                    <BarChart3 className="w-4 h-4" /> Invested
                  </div>
                  <p className="text-xl font-bold text-blue-400">
                    {formatCurrency(portfolio.total_holdings_value || 0, "USD")}
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-300 text-sm mb-1">
                    <Percent className="w-4 h-4" /> All-Time Return
                  </div>
                  <p className={`text-xl font-bold ${(portfolio.total_gain_loss || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(portfolio.total_gain_loss || 0) >= 0 ? "+" : ""}{portfolio.total_gain_loss_pct?.toFixed(2) || "0.00"}%
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-300 text-sm mb-1">
                    {(portfolio.total_gain_loss || 0) >= 0 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )} All-Time P/L
                  </div>
                  <p className={`text-xl font-bold ${(portfolio.total_gain_loss || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(portfolio.total_gain_loss || 0) >= 0 ? "+" : ""}{formatCurrency(Math.abs(portfolio.total_gain_loss || 0), "USD")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TABS */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 h-auto">
              <TabsTrigger value="portfolio" className="active:scale-95 transition-transform flex-col sm:flex-row gap-1 py-2 px-1 sm:px-3" data-testid="portfolio-tab">
                <Wallet className="w-4 h-4" /> <span className="hidden sm:inline">Holdings</span>
              </TabsTrigger>
              <TabsTrigger value="trade" className="active:scale-95 transition-transform flex-col sm:flex-row gap-1 py-2 px-1 sm:px-3" data-testid="trade-tab">
                <BarChart3 className="w-4 h-4" /> <span className="hidden sm:inline">Trade</span>
              </TabsTrigger>
              <TabsTrigger value="watchlist" className="active:scale-95 transition-transform flex-col sm:flex-row gap-1 py-2 px-1 sm:px-3 relative" data-testid="watchlist-tab">
                <Eye className="w-4 h-4" /> <span className="hidden sm:inline">Watchlist</span>
                {watchlist.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 sm:relative sm:top-0 sm:right-0 sm:ml-1 bg-amber-500 text-xs h-4 w-4 sm:h-auto sm:w-auto p-0 sm:px-1 flex items-center justify-center">{watchlist.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="leaderboard" className="active:scale-95 transition-transform flex-col sm:flex-row gap-1 py-2 px-1 sm:px-3" data-testid="leaderboard-tab">
                <Trophy className="w-4 h-4" /> <span className="hidden sm:inline">Rank</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="active:scale-95 transition-transform flex-col sm:flex-row gap-1 py-2 px-1 sm:px-3" data-testid="history-tab">
                <Clock className="w-4 h-4" /> <span className="hidden sm:inline">History</span>
              </TabsTrigger>
            </TabsList>

            {/* HOLDINGS TAB */}
            <TabsContent value="portfolio" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Your Holdings ({portfolio.holdings?.length || 0} stocks)</span>
                    {portfolio.holdings?.length > 0 && (
                      <Badge variant="outline">
                        Invested: {formatCurrency(portfolio.total_holdings_value || 0, "USD")} {showInCAD ? "CAD" : "USD"}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {portfolio.holdings?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="mb-4">No holdings yet. Start trading to build your portfolio!</p>
                      <Button onClick={() => setActiveTab("trade")} className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform">
                        Start Trading
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {portfolio.holdings?.map((holding) => {
                        const stockData = stocks.find(s => s.symbol === holding.symbol) || { symbol: holding.symbol, price: holding.current_price, name: holding.symbol };
                        
                        return (
                          <motion.div
                            key={holding.symbol}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => {
                              selectStock(stockData);
                              setActiveTab("trade");
                            }}
                            className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 cursor-pointer transition-all"
                            data-testid={`holding-${holding.symbol}`}
                          >
                            <div className="flex items-center justify-between flex-wrap gap-4">
                              <div className="flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-lg text-slate-800">{holding.symbol}</span>
                                  <Badge className={holding.symbol.includes(".TO") ? "bg-red-500" : "bg-blue-500"}>
                                    {holding.symbol.includes(".TO") ? "🇨🇦 TSX" : "🇺🇸 US"}
                                  </Badge>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      isInWatchlist(holding.symbol) ? removeFromWatchlist(holding.symbol) : addToWatchlist(holding.symbol);
                                    }}
                                    className="text-amber-500 hover:text-amber-600 active:scale-90 transition-transform"
                                  >
                                    {isInWatchlist(holding.symbol) ? <Star className="w-4 h-4 fill-amber-500" /> : <StarOff className="w-4 h-4" />}
                                  </button>
                                  <Eye className="w-4 h-4 text-slate-400" title="Click to view details" />
                                </div>
                                <p className="text-sm text-slate-500">
                                  {holding.shares} shares @ ${holding.avg_cost.toFixed(2)} avg
                                </p>
                              </div>
                              
                              <div className="text-right">
                                <p className="font-bold text-lg text-slate-800">
                                  {formatCurrency(holding.current_value, holding.symbol.includes(".TO") ? "CAD" : "USD")}
                                </p>
                                <div className="flex items-center justify-end gap-2 text-sm">
                                  <span className={(holding.day_change_pct || 0) >= 0 ? "text-emerald-600" : "text-red-600"}>
                                    Today: {(holding.day_change_pct || 0) >= 0 ? "+" : ""}{(holding.day_change_pct || 0).toFixed(2)}%
                                  </span>
                                  <span className="text-slate-400">|</span>
                                  <span className={holding.gain_loss >= 0 ? "text-emerald-600" : "text-red-600"}>
                                    Total: {holding.gain_loss >= 0 ? "+" : ""}{holding.gain_loss_pct?.toFixed(2)}%
                                  </span>
                                </div>
                              </div>
                              
                              <div className={`px-4 py-2 rounded-lg ${holding.gain_loss >= 0 ? "bg-emerald-50" : "bg-red-50"}`}>
                                <p className="text-xs text-slate-500">Total P/L</p>
                                <p className={`font-bold ${holding.gain_loss >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                  {holding.gain_loss >= 0 ? "+" : ""}{formatCurrency(Math.abs(holding.gain_loss), holding.symbol.includes(".TO") ? "CAD" : "USD")}
                                </p>
                              </div>
                              
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); quickBuyMore(holding); }}
                                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 active:scale-95 transition-transform"
                                >
                                  Buy More
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); quickSell(holding); }}
                                  className="text-red-600 border-red-300 hover:bg-red-50 active:scale-95 transition-transform"
                                >
                                  Sell
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TRADE TAB */}
            <TabsContent value="trade" className="mt-4">
              {/* Research Disclaimer Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Do Your Own Research (DYOR)</p>
                    <p className="text-xs text-amber-700 mt-1">
                      This simulator uses real market prices for educational purposes. Before investing real money, always research stocks thoroughly using official sources like company filings, financial news, and regulated platforms.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <Card>
                  <CardHeader className="pb-2 sm:pb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                      <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                        Stocks 
                        <Badge variant="secondary" className="font-normal text-xs">
                          {filteredStocks.length}/{stocks.length}
                        </Badge>
                      </CardTitle>
                      <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); }}>
                        <SelectTrigger className="w-full sm:w-[140px]">
                          <Filter className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All ({stocks.length})</SelectItem>
                          <SelectItem value="tech">Tech ({groupedStocks.tech?.length || 0})</SelectItem>
                          <SelectItem value="finance">Finance ({groupedStocks.finance?.length || 0})</SelectItem>
                          <SelectItem value="healthcare">Healthcare ({groupedStocks.healthcare?.length || 0})</SelectItem>
                          <SelectItem value="canadian">Canadian 🇨🇦 ({groupedStocks.canadian?.length || 0})</SelectItem>
                          <SelectItem value="etf">ETFs ({groupedStocks.etf?.length || 0})</SelectItem>
                          <SelectItem value="gold">Gold 🥇 ({groupedStocks.gold?.length || 0})</SelectItem>
                          <SelectItem value="silver">Silver 🥈 ({groupedStocks.silver?.length || 0})</SelectItem>
                          <SelectItem value="crypto">Crypto ({groupedStocks.crypto?.length || 0})</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search stocks..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (aiSearchResults) setAiSearchResults(null);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleAISearch()}
                          className="pl-10 text-sm"
                          data-testid="stock-search-input"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAISearch}
                        disabled={aiSearching || !searchQuery.trim()}
                        className="whitespace-nowrap px-2 sm:px-3"
                      >
                        {aiSearching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">AI Search</span>
                          </>
                        )}
                      </Button>
                    </div>
                    {searchQuery && !aiSearchResults && (
                      <p className="text-xs text-slate-500">
                        Showing results for &quot;{searchQuery}&quot; - {filteredStocks.length} matches
                      </p>
                    )}
                    {aiSearchResults && (
                      <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">AI Search Results</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={clearAISearch} className="text-purple-600 h-6 px-2">
                            Clear
                          </Button>
                        </div>
                        {aiExplanation && (
                          <p className="text-xs text-purple-700 mb-2">{aiExplanation}</p>
                        )}
                        <p className="text-xs text-purple-600">Found {aiSearchResults.length} matching stocks</p>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="max-h-[600px] overflow-y-auto">
                    {stocksLoading || aiSearching ? (
                      <FunFactsLoader message={aiSearching ? "AI is searching..." : "Loading stocks..."} className="py-4" />
                    ) : aiSearchResults ? (
                      <div className="space-y-2">
                        {aiSearchResults.map((stock) => {
                          const holding = portfolio.holdings?.find(h => h.symbol === stock.symbol);
                          const displayPrice = showInCAD && !stock.symbol.includes(".TO")
                            ? stock.price * USD_TO_CAD
                            : stock.price;
                          
                          return (
                            <motion.div
                              key={stock.symbol}
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                              onClick={() => selectStock(stock)}
                              className={`p-3 rounded-lg cursor-pointer transition-all duration-150 ${
                                selectedStock?.symbol === stock.symbol
                                  ? "bg-purple-100 border-2 border-purple-500"
                                  : "bg-slate-50 hover:bg-slate-100"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-slate-800">{stock.symbol}</span>
                                      {holding && (
                                        <Badge variant="outline" className="text-xs">
                                          Own {holding.shares} shares
                                        </Badge>
                                      )}
                                    </div>
                                    <span className="text-xs text-slate-500">{stock.name}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold">
                                    ${displayPrice?.toFixed(2)} {showInCAD ? "CAD" : (stock.symbol.includes(".TO") ? "CAD" : "USD")}
                                  </p>
                                  <p className={`text-sm ${stock.change_pct >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                                    {stock.change_pct >= 0 ? "+" : ""}{stock.change_pct?.toFixed(2)}%
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    ) : filteredStocks.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No stocks found for &quot;{searchQuery}&quot;.</p>
                        <p className="text-xs mt-2">Try AI Search for natural language queries like &quot;tech stocks&quot; or &quot;Canadian banks&quot;</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredStocks.map((stock) => {
                          const holding = portfolio.holdings?.find(h => h.symbol === stock.symbol);
                          
                          return (
                            <motion.div
                              key={stock.symbol}
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                              onClick={() => selectStock(stock)}
                              className={`p-3 rounded-lg cursor-pointer transition-all duration-150 ${
                                selectedStock?.symbol === stock.symbol
                                  ? "bg-emerald-100 border-2 border-emerald-500"
                                  : holding
                                  ? "bg-blue-50 border border-blue-200"
                                  : "bg-slate-50 hover:bg-slate-100"
                              }`}
                              data-testid={`stock-${stock.symbol}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-semibold text-slate-800">{stock.symbol}</p>
                                    <Badge className={`text-xs ${stock.type === "ETF" ? "bg-purple-500" : stock.symbol.includes(".TO") ? "bg-red-500" : "bg-blue-500"}`}>
                                      {stock.type === "ETF" ? "ETF" : stock.symbol.includes(".TO") ? "TSX" : "US"}
                                    </Badge>
                                    {holding && (
                                      <Badge variant="outline" className="text-xs bg-blue-100">
                                        Own: {holding.shares}
                                      </Badge>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); isInWatchlist(stock.symbol) ? removeFromWatchlist(stock.symbol) : addToWatchlist(stock.symbol); }}
                                      className="text-amber-500 hover:text-amber-600 active:scale-90 transition-transform"
                                    >
                                      {isInWatchlist(stock.symbol) ? <Star className="w-4 h-4 fill-amber-500" /> : <StarOff className="w-4 h-4" />}
                                    </button>
                                  </div>
                                  <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                    {stock.name}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold">
                                    {formatCurrency(stock.price, stock.currency)}
                                  </p>
                                  <p
                                    className={`text-xs flex items-center justify-end gap-1 ${
                                      (stock.change_pct || stock.change) >= 0 ? "text-emerald-600" : "text-red-600"
                                    }`}
                                  >
                                    {(stock.change_pct || stock.change) >= 0 ? (
                                      <ArrowUp className="w-3 h-3" />
                                    ) : (
                                      <ArrowDown className="w-3 h-3" />
                                    )}
                                    {(stock.change_pct || stock.change) >= 0 ? "+" : ""}
                                    {(stock.change_pct || stock.change)?.toFixed(2)}%
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* TRADE PANEL */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {selectedStock ? `${selectedStock.symbol} Details` : "Select a Stock"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedStock ? (
                      <div className="space-y-4">
                        {/* Stock Header */}
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold">{selectedStock.symbol}</span>
                              <Badge className={selectedStock.type === "ETF" ? "bg-purple-500" : selectedStock.symbol.includes(".TO") ? "bg-red-500" : "bg-blue-500"}>
                                {selectedStock.type === "ETF" ? "ETF" : selectedStock.symbol.includes(".TO") ? "🇨🇦 CAD" : "🇺🇸 USD"}
                              </Badge>
                            </div>
                            <Badge className={(selectedStock.change_pct || selectedStock.change) >= 0 ? "bg-emerald-500" : "bg-red-500"}>
                              {(selectedStock.change_pct || selectedStock.change) >= 0 ? "+" : ""}
                              {(selectedStock.change_pct || selectedStock.change)?.toFixed(2)}%
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{selectedStock.name}</p>
                          <p className="text-3xl font-bold text-slate-800 mt-2">
                            {formatCurrency(selectedStock.price, selectedStock.currency)}
                            <span className="text-sm ml-2 text-slate-500">{showInCAD ? "CAD" : selectedStock.currency}</span>
                          </p>
                        </div>

                        {/* Detailed Stock Info - Only show if data is reliable */}
                        {detailsLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                            <span className="ml-2 text-slate-600">Loading details...</span>
                          </div>
                        ) : selectedStockDetails?.has_reliable_data ? (
                          <div className="bg-white border rounded-lg p-4 space-y-3">
                            {/* Price Info */}
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <div>
                                <p className="text-slate-500">Open</p>
                                <p className="font-semibold">${selectedStockDetails.open?.toFixed(2) || "N/A"}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">High</p>
                                <p className="font-semibold text-emerald-600">${selectedStockDetails.day_high?.toFixed(2) || "N/A"}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Low</p>
                                <p className="font-semibold text-red-600">${selectedStockDetails.day_low?.toFixed(2) || "N/A"}</p>
                              </div>
                            </div>

                            <hr className="border-slate-200" />

                            {/* 52-Week Range */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-slate-500">52-wk High</p>
                                <p className="font-semibold text-emerald-600">${selectedStockDetails.fifty_two_week_high?.toFixed(2) || "N/A"}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">52-wk Low</p>
                                <p className="font-semibold text-red-600">${selectedStockDetails.fifty_two_week_low?.toFixed(2) || "N/A"}</p>
                              </div>
                            </div>

                            <hr className="border-slate-200" />

                            {/* Valuation */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-slate-500">Mkt Cap</p>
                                <p className="font-semibold">{formatLargeNumber(selectedStockDetails.market_cap)}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">P/E Ratio</p>
                                <p className="font-semibold">{selectedStockDetails.pe_ratio?.toFixed(2) || "N/A"}</p>
                              </div>
                            </div>

                            {/* Dividends - using normalized percentage */}
                            {(selectedStockDetails.dividend_yield_pct || selectedStockDetails.dividend_rate) && (
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                  <p className="text-slate-500">Dividend Yield</p>
                                  <p className="font-semibold">
                                    {selectedStockDetails.dividend_yield_pct ? `${selectedStockDetails.dividend_yield_pct.toFixed(2)}%` : "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Annual Dividend</p>
                                  <p className="font-semibold">
                                    {selectedStockDetails.dividend_rate ? `$${selectedStockDetails.dividend_rate.toFixed(2)}` : "N/A"}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* ETF Specific - MER */}
                            {selectedStock.type === "ETF" && selectedStockDetails.expense_ratio_pct && (
                              <>
                                <hr className="border-slate-200" />
                                <div className="text-sm">
                                  <p className="text-slate-500">MER (Expense Ratio)</p>
                                  <p className="font-semibold">{selectedStockDetails.expense_ratio_pct.toFixed(2)}%</p>
                                </div>
                              </>
                            )}

                            {/* Research Disclaimer */}
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                              <p className="text-xs text-amber-800 flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>
                                  <strong>Do your own research.</strong> Data may be delayed. Always verify information on official sources before making investment decisions.
                                </span>
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Info className="w-4 h-4" />
                              Detailed data unavailable. Please research this stock on official financial websites before investing.
                            </p>
                          </div>
                        )}

                        {/* AI-Powered Insights from Perplexity */}
                        {insightsLoading ? (
                          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                              <span className="text-sm text-purple-700">Loading AI insights...</span>
                            </div>
                          </div>
                        ) : stockInsights?.insights_available ? (
                          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                                <Sparkles className="w-4 h-4" />
                                AI Market Insights
                              </h4>
                              <Badge className="bg-purple-500 text-white text-xs">
                                Powered by Perplexity
                              </Badge>
                            </div>
                            
                            {/* Price Context */}
                            {stockInsights.current_price_context && (
                              <div className="text-sm text-purple-900">
                                <p>{stockInsights.current_price_context}</p>
                              </div>
                            )}
                            
                            {/* Recent News */}
                            {stockInsights.recent_news?.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold text-purple-700 uppercase">Recent News</p>
                                {stockInsights.recent_news.slice(0, 2).map((news, idx) => (
                                  <div key={idx} className="bg-white/60 rounded p-2">
                                    <div className="flex items-start gap-2">
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        news.impact === 'positive' ? 'bg-emerald-100 text-emerald-700' :
                                        news.impact === 'negative' ? 'bg-red-100 text-red-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {news.impact === 'positive' ? '↑' : news.impact === 'negative' ? '↓' : '→'}
                                      </span>
                                      <div>
                                        <p className="text-xs font-medium text-slate-800">{news.headline}</p>
                                        <p className="text-xs text-slate-600">{news.summary}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Analyst Sentiment */}
                            {stockInsights.analyst_sentiment && (
                              <div className="bg-white/60 rounded p-2">
                                <p className="text-xs font-semibold text-purple-700">Analyst View</p>
                                <p className="text-xs text-slate-700">{stockInsights.analyst_sentiment}</p>
                              </div>
                            )}
                            
                            {/* Beginner Note */}
                            {stockInsights.beginner_note && (
                              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                                <p className="text-xs text-emerald-800">
                                  <strong>💡 Beginner Tip:</strong> {stockInsights.beginner_note}
                                </p>
                              </div>
                            )}
                            
                            {/* Risk Factors */}
                            {stockInsights.risk_factors?.length > 0 && (
                              <div className="text-xs">
                                <p className="font-semibold text-red-700 mb-1">⚠️ Risks to Consider:</p>
                                <ul className="list-disc list-inside text-slate-600 space-y-0.5">
                                  {stockInsights.risk_factors.slice(0, 3).map((risk, idx) => (
                                    <li key={idx}>{risk}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : null}

                        {portfolio.holdings?.find(h => h.symbol === selectedStock.symbol) && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>You own:</strong> {portfolio.holdings.find(h => h.symbol === selectedStock.symbol).shares} shares
                              <span className="ml-2 text-blue-600">
                                ({formatCurrency(portfolio.holdings.find(h => h.symbol === selectedStock.symbol).current_value, selectedStock.currency)})
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Trade Mode Toggle */}
                        <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                          <button
                            onClick={() => { setTradeMode("shares"); setTradeAmount(""); }}
                            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                              tradeMode === "shares" 
                                ? "bg-white shadow text-slate-800" 
                                : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            Buy by Shares
                          </button>
                          <button
                            onClick={() => { setTradeMode("cad"); setTradeAmount(""); }}
                            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                              tradeMode === "cad" 
                                ? "bg-white shadow text-slate-800" 
                                : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            Buy by $ CAD
                          </button>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-slate-700">
                            {tradeMode === "shares" ? "Number of Shares" : "Amount in CAD ($)"}
                          </label>
                          <Input
                            type="number"
                            min="0.01"
                            step={tradeMode === "shares" ? "0.01" : "1"}
                            placeholder={tradeMode === "shares" ? "Enter shares (e.g., 5)" : "Enter CAD amount (e.g., 500)"}
                            value={tradeAmount}
                            onChange={(e) => setTradeAmount(e.target.value)}
                            className="mt-1"
                            data-testid="shares-input"
                          />
                          {tradeAmount && selectedStock && (
                            <div className="text-sm text-slate-500 mt-1 space-y-1">
                              {tradeMode === "shares" ? (
                                <>
                                  <p>Total Cost: <span className="font-semibold">{formatCurrency(parseFloat(tradeAmount) * selectedStock.price, selectedStock.currency)}</span></p>
                                </>
                              ) : (
                                <>
                                  <p>You&apos;ll get: <span className="font-semibold">{(parseFloat(tradeAmount) / (selectedStock.price * (selectedStock.currency === "USD" ? USD_TO_CAD : 1))).toFixed(4)} shares</span></p>
                                  <p className="text-xs text-slate-400">
                                    @ {formatCurrency(selectedStock.price, selectedStock.currency)} per share
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            onClick={() => {
                              if (tradeMode === "cad" && tradeAmount && selectedStock) {
                                // Convert CAD to shares
                                const priceInCAD = selectedStock.price * (selectedStock.currency === "USD" ? USD_TO_CAD : 1);
                                const sharesToBuy = parseFloat(tradeAmount) / priceInCAD;
                                setTradeAmount(sharesToBuy.toFixed(4));
                                setTradeMode("shares");
                              }
                              executeTrade("buy");
                            }}
                            disabled={tradeLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 h-12 active:scale-95 transition-transform"
                            data-testid="buy-btn"
                          >
                            {tradeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <>
                                <ArrowUp className="w-4 h-4 mr-2" /> Buy
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => executeTrade("sell")}
                            disabled={tradeLoading}
                            variant="outline"
                            className="border-red-500 text-red-600 hover:bg-red-50 h-12 active:scale-95 transition-transform"
                            data-testid="sell-btn"
                          >
                            {tradeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <>
                                <ArrowDown className="w-4 h-4 mr-2" /> Sell
                              </>
                            )}
                          </Button>
                        </div>

                        <div className="bg-amber-50 p-3 rounded-lg flex items-start gap-2">
                          <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                          <div className="text-xs text-amber-700">
                            <p><strong>Cash available:</strong> {formatCurrency(portfolio.cash, "USD")} {showInCAD ? "CAD" : "USD"}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Select a stock from the list to trade</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* WATCHLIST TAB */}
            <TabsContent value="watchlist" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-amber-500" />
                    Your Watchlist ({watchlist.length} stocks)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {watchlist.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="mb-4">No stocks in your watchlist yet.</p>
                      <p className="text-sm">Click the star icon ⭐ on any stock to add it here!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {watchlist.map((item) => (
                        <motion.div
                          key={item.symbol}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-amber-300 transition-colors"
                        >
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex-1 min-w-[200px]">
                              <div className="flex items-center gap-2">
                                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                <span className="font-bold text-lg text-slate-800">{item.symbol}</span>
                                <Badge className={item.type === "ETF" ? "bg-purple-500" : item.symbol.includes(".TO") ? "bg-red-500" : "bg-blue-500"}>
                                  {item.type === "ETF" ? "ETF" : item.symbol.includes(".TO") ? "🇨🇦 TSX" : "🇺🇸 US"}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500">{item.name}</p>
                              <p className="text-xs text-slate-400">
                                Added @ ${item.price_when_added?.toFixed(2)} on {new Date(item.added_at).toLocaleDateString()}
                              </p>
                            </div>
                            
                            <div className="text-right">
                              <p className="font-bold text-lg text-slate-800">
                                {formatCurrency(item.current_price, item.currency)}
                              </p>
                              <div className="flex items-center justify-end gap-2 text-sm">
                                <span className={(item.change_pct || 0) >= 0 ? "text-emerald-600" : "text-red-600"}>
                                  Today: {(item.change_pct || 0) >= 0 ? "+" : ""}{(item.change_pct || 0).toFixed(2)}%
                                </span>
                              </div>
                            </div>
                            
                            <div className={`px-4 py-2 rounded-lg ${(item.change_since_added_pct || 0) >= 0 ? "bg-emerald-50" : "bg-red-50"}`}>
                              <p className="text-xs text-slate-500">Since Added</p>
                              <p className={`font-bold ${(item.change_since_added_pct || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                {(item.change_since_added_pct || 0) >= 0 ? "+" : ""}{(item.change_since_added_pct || 0).toFixed(2)}%
                              </p>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => { setSelectedStock(stocks.find(s => s.symbol === item.symbol) || item); setActiveTab("trade"); }}
                                className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform"
                              >
                                Trade
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeFromWatchlist(item.symbol)}
                                className="text-red-600 border-red-300 hover:bg-red-50 active:scale-95 transition-transform"
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* LEADERBOARD TAB */}
            <TabsContent value="leaderboard" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Top 10 Traders - Monthly Leaderboard
                  </CardTitle>
                  <p className="text-sm text-slate-500">Rankings based on portfolio gain percentage</p>
                </CardHeader>
                <CardContent>
                  {leaderboard.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No traders on the leaderboard yet. Be the first!</p>
                      <p className="text-sm mt-2">Start trading to appear on the leaderboard</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {leaderboard.slice(0, 10).map((trader, index) => (
                        <div
                          key={trader.user_id || index}
                          className={`flex items-center justify-between p-4 rounded-lg ${
                            trader.user_id === user?.id
                              ? "bg-emerald-50 border-2 border-emerald-500"
                              : index === 0
                              ? "bg-amber-50 border border-amber-200"
                              : index === 1
                              ? "bg-slate-100 border border-slate-200"
                              : index === 2
                              ? "bg-orange-50 border border-orange-200"
                              : "bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                index === 0
                                  ? "bg-amber-500 text-white"
                                  : index === 1
                                  ? "bg-slate-400 text-white"
                                  : index === 2
                                  ? "bg-orange-400 text-white"
                                  : "bg-slate-200 text-slate-600"
                              }`}
                            >
                              {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : trader.rank || index + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">
                                {trader.display_name || trader.username || "Anonymous Trader"}
                                {trader.user_id === user?.id && (
                                  <Badge className="ml-2 bg-emerald-500">You</Badge>
                                )}
                              </p>
                              <p className="text-sm text-slate-500">
                                {trader.holdings_count} holdings
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">${trader.total_value?.toLocaleString('en-CA', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</p>
                            <p
                              className={`text-sm font-medium ${
                                (trader.gain_percentage || trader.gain_pct || 0) >= 0 ? "text-emerald-600" : "text-red-600"
                              }`}
                            >
                              {(trader.gain_percentage || trader.gain_pct || 0) >= 0 ? "+" : ""}
                              {(trader.gain_percentage || trader.gain_pct || 0)?.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* HISTORY TAB */}
            <TabsContent value="history" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Transaction History</CardTitle>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No transactions yet. Start trading!</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {transactions.map((tx) => (
                        <div
                          key={tx.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                tx.action === "buy" ? "bg-emerald-100" : "bg-red-100"
                              }`}
                            >
                              {tx.action === "buy" ? (
                                <ArrowUp className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <ArrowDown className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">
                                {tx.action === "buy" ? "Bought" : "Sold"} <strong>{tx.symbol}</strong>
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(tx.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{tx.shares} shares</p>
                            <p className="text-sm text-slate-500">@ ${tx.price.toFixed(2)}</p>
                            <p className={`text-sm font-medium ${tx.action === "buy" ? "text-red-600" : "text-emerald-600"}`}>
                              {tx.action === "buy" ? "-" : "+"}${tx.total_value.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default StockSimulator;
